import React, { useEffect, useState } from "react";
import { Formik } from "formik";
import _ from "lodash";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import CustomFilterInput from "../../../shared/CustomFilterInput";
import AppButton from "../../../shared/AppButton";
import { FaFilter, FaUndo } from "react-icons/fa";

function PaymentsFilter({
  refetch,
  setPageNo,
  setLimit,
  setFromDate,
  setToDate,
  setRefetchFilterVariables,
  storageKey = "",
}) {
  const { t } = useTranslation();
  const [payers, setPayers] = useState([]);
  const navigate = useNavigate();
  const currentDate = new Date();

  const defaultFromDate = `${currentDate.getFullYear()}-${String(
    currentDate.getMonth() + 1,
  ).padStart(2, "0")}-01`;
  const defaultToDate = `${currentDate.getFullYear()}-${String(
    currentDate.getMonth() + 1,
  ).padStart(2, "0")}-${new Date(
    currentDate.getFullYear(),
    currentDate.getMonth() + 1,
    0,
  ).getDate()}`;

  // Load saved filters from sessionStorage
  const [savedFilters, setSavedFilters] = useState(() => {
    if (storageKey) {
      const saved = sessionStorage.getItem(storageKey);
      if (saved) {
        try {
          return JSON.parse(saved);
        } catch (error) {
          console.error("Error loading saved filters:", error);
        }
      }
    }
    return null;
  });

  // Load payers from localStorage
  useEffect(() => {
    const payersData = JSON.parse(localStorage.getItem("payersData"));
    if (payersData && payersData.length > 0) {
      setPayers(payersData);
    } else {
      navigate("/dashboard");
    }
  }, []);

  const statuses = [
    { id: "pending", name: t("Pending") },
    { id: "cleared", name: t("Cleared") },
  ];

  const addData = (values) => {
    setPageNo(1);
    setFromDate(values.fromDate);
    setToDate(values.toDate);

    const obj = {
      from_date: values.fromDate,
      to_date: values.toDate,
      payer_id: values.payer,
      provider_name_en: values.providerNameEn,
      confirmation_status: values.status,
      limit: 10,
      page: 1,
    };

    setRefetchFilterVariables({ ...obj });

    // Save to sessionStorage
    if (storageKey) {
      sessionStorage.setItem(storageKey, JSON.stringify({ ...obj }));
    }

    refetch({
      input: Object.keys(obj).reduce((acc, key) => {
        const value = obj[key];
        if (value !== "" && value !== null) {
          acc[key] = value;
        }
        return acc;
      }, {}),
    });
  };

  return (
    <Formik
      initialValues={{
        fromDate: savedFilters?.from_date || defaultFromDate,
        toDate: savedFilters?.to_date || defaultToDate,
        payer: savedFilters?.payer_id || "",
        providerNameEn: savedFilters?.provider_name_en || "",
        status: savedFilters?.confirmation_status || "",
      }}
      enableReinitialize={true}
      validate={() => ({})}
      onSubmit={(values) => addData(values)}
    >
      {({ values, handleChange, handleBlur, handleSubmit, resetForm }) => (
        <div className="w-full">
          <div className="flex flex-col md:flex-row md:flex-wrap lg:flex-nowrap md:items-end justify-end gap-2">
            <CustomFilterInput
              label={t("From Date")}
              name="fromDate"
              type="date"
              value={values.fromDate}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={t("From Date")}
              className="w-full md:w-[160px]"
            />
            <CustomFilterInput
              label={t("To Date")}
              name="toDate"
              type="date"
              value={values.toDate}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={t("To Date")}
              className="w-full md:w-[160px]"
            />
            <CustomFilterInput
              label={t("Select Payer")}
              name="payer"
              isSelect
              value={values.payer}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={t("Select Payer")}
              renderOptions={() => (
                <>
                  <option value="">{t("Select Payer")}</option>
                  {payers.map((category) => (
                    <optgroup
                      key={category.id}
                      label={
                        category?.name_en
                          ? `${category.name_en} (${category.license_id})`
                          : ""
                      }
                    >
                      {category.payers.map((insurance, idx) => (
                        <option
                          key={`${insurance.id}-${idx}`}
                          value={insurance.id}
                        >
                          {insurance?.name_en} ({insurance?.license_id})
                        </option>
                      ))}
                    </optgroup>
                  ))}
                </>
              )}
              className="w-full md:w-[160px]"
            />
            <CustomFilterInput
              label={t("Provider Name")}
              name="providerNameEn"
              type="text"
              placeholder={t("Enter Provider Name")}
              value={values.providerNameEn}
              onChange={handleChange}
              onBlur={handleBlur}
              className="w-full md:w-[160px]"
            />
            <CustomFilterInput
              label={t("Status")}
              name="status"
              isSelect
              value={values.status}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={t("Select Status")}
              options={statuses}
              className="w-full md:w-[160px]"
            />

            <div className="flex justify-end gap-2 items-end">
              <AppButton
                // label={t("Reset")}
                variant="reset"
                startIcon={<FaUndo className="!text-[12px]" />}
                onClick={() => {
                  resetForm();
                  setSavedFilters(null);
                  if (storageKey) {
                    sessionStorage.removeItem(storageKey);
                  }
                  setFromDate(defaultFromDate);
                  setToDate(defaultToDate);
                  setRefetchFilterVariables({
                    limit: 10,
                    from_date: defaultFromDate,
                    to_date: defaultToDate,
                  });
                  refetch({
                    input: {
                      from_date: defaultFromDate,
                      to_date: defaultToDate,
                      limit: 10,
                      page: 1,
                    },
                  });
                }}
              />
              <AppButton
                // label={t("Apply Filter")}
                startIcon={<FaFilter className="!text-[12px]" />}
                onClick={() => handleSubmit()}
              />
            </div>
          </div>
        </div>
      )}
    </Formik>
  );
}

export default PaymentsFilter;
